import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, useIntl } from 'react-intl';
import isEqual from 'lodash-es/isEqual';
import moment from 'moment';

import intlShape from '@/shared/utils/intlShape';
import RegistrationComponent from '@/shared/components/registration/RegistrationComponent';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import AuthService, { functionalities } from '@/shared/services/AuthService';

import {
  createEmptyRegistration,
  executeAction,
  isRegistrationValid,
  registrationShape,
} from './registrationHelpers';
import { createOrUpdateInteraction } from './resourceApi';
import messages from './registrationMessages';

class RegistrationModal extends Component {
  state = {
    registration: null,
    overtimeRemaining: null,
  };

  componentDidMount() {
    const { registration: initRegistration } = this.props;

    const registration = createEmptyRegistration(initRegistration);

    this.setState({ registration, overtimeRemaining: null });
    if (registration.type === 4) {
      api.base
        .get(`${config.appUrls.api}/api/Employee/MyOvertimeRemaining?userId=${registration.userId}`)
        .then(({ data }) => {
          this.setState({ overtimeRemaining: data });
        });
    }
  }

  doRegistrationAction = (action, value) => {
    this.setState(({ registration }) => {
      const newValue = executeAction(registration, action, value);
      if (isEqual(newValue, registration)) return {};
      return { registration: newValue };
    });
  };
  saveRegistration = () => {
    if (this.isFormInvalid()) return;
    const { intl, onSave } = this.props;
    const { registration } = this.state;

    const model = { ...registration };
    delete model.orgInteraction;
    if (registration.id) {
      return api.base.put(`${config.appUrls.api}/api/Registration/PUT`, [model]).then(
        ({ data }) => {
          const [saveRegistration = null] = data;
          if (saveRegistration) {
            ToastrService.success(intl.formatMessage(messages.RegistrationModified));
            createOrUpdateInteraction(registration, saveRegistration.group).then(() => {
              onSave(saveRegistration);
            });
          }
        },
        error => {
          handleHttpError(intl)(error);
        },
      );
    }
    return api.base.post(`${config.appUrls.api}/api/Registration/POST`, [model]).then(
      ({ data }) => {
        const [saveRegistration = null] = data;
        if (saveRegistration) {
          let content = (model.resource && model.resource.label) || '';

          if (
            (model.type === 0 || model.type === 3 || model.type === 4 || model.type === 5) &&
            model.durationMinutes > 0
          ) {
            const formattedDuration = moment
              .duration(model.durationMinutes, 'minutes')
              .format('hh:mm', {
                trim: false,
              });
            content = `${formattedDuration} ${content}`;
          }

          ToastrService.info(content, intl.formatMessage(messages.Registered));
          createOrUpdateInteraction(registration, saveRegistration.group).then(() => {
            onSave(saveRegistration);
          });
        }
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };
  isFormInvalid = () => {
    const { registration } = this.state;
    const { registrationOptions } = this.props;
    if (!isRegistrationValid(registration, registrationOptions)) return true;
    return false;
  };
  showValidation = () => this.setState({ showValidation: true });
  hasSavePermission = () => {
    const { registration } = this.state;
    if (!registration) return false;
    if (registration.userId && registration.userId !== AuthService.getUserId())
      return AuthService.hasPermission({
        functionality: functionalities.RegistrationOthers,
        privilege: 'Update',
      });
    return AuthService.hasPermission({
      functionality: functionalities.RegistrationOwn,
      privilege: 'Update',
    });
  };
  getModalTitle = () => {
    const { registration } = this.state;
    const { intl } = this.props;

    if (registration) {
      const isNew = !registration.id;
      switch (registration.type) {
        case 0:
        case 1:
        case 2:
          return intl.formatMessage(isNew ? messages.AddRegistration : messages.EditRegistration);
        case 3:
          return intl.formatMessage(isNew ? messages.AddAbsence : messages.EditAbsence);
        case 4:
          return intl.formatMessage(isNew ? messages.AddRecuperation : messages.EditRecuperation);
        case 5:
          return intl.formatMessage(isNew ? messages.AddTraining : messages.EditTraining);
      }
    }

    return intl.formatMessage(messages.Registration);
  };

  render() {
    const {
      show,
      intl,
      onClose,
      registrationOptions,
      defaultResourceId,
      mainTaskId,
      okLabel,
    } = this.props;
    const { registration, showValidation, overtimeRemaining } = this.state;
    const hasRegistrationPermission = this.hasSavePermission();
    return (
      <OkCancelModal
        title={this.getModalTitle()}
        show={show}
        onClose={onClose}
        disabled={!hasRegistrationPermission || (showValidation && this.isFormInvalid())}
        onSave={this.saveRegistration}
        onHoverSave={this.showValidation}
        okLabel={okLabel}
      >
        <div className="form-horizontal">
          {registration ? (
            <RegistrationComponent
              registration={registration}
              overtimeRemaining={overtimeRemaining}
              onAction={this.doRegistrationAction}
              options={registrationOptions}
              showValidation={showValidation}
              defaultResourceId={defaultResourceId}
              mainTaskId={mainTaskId}
            />
          ) : null}
        </div>
      </OkCancelModal>
    );
  }
}

RegistrationModal.propTypes = {
  registration: registrationShape,
  onSave: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  onClose: PropTypes.func.isRequired,
  show: PropTypes.bool,
  registrationOptions: PropTypes.shape({}),
  // Select this resource on init (if available)
  defaultResourceId: PropTypes.string,
  // If the mainTaskId is provided, only this mainTask and its subtasks can be selected
  mainTaskId: PropTypes.string,
  training: PropTypes.object,
};

RegistrationModal.defaultProps = {
  show: false,
  defaultResourceId: null,
  mainTaskId: null,
  registration: null,
  registrationOptions: {},
};
export default injectIntl(RegistrationModal);
